import React from "react";

import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { Column } from "components/Layout";
import { Text } from "components/Text";

import {
  ReviewCard,
  ReviewCardTitle,
  ReviewFieldGrid,
  ReviewFieldLabel,
} from "components/ReviewCard";

import { LivestockSalePricingTypeDisplayNameLkp } from "constants/pricingTypes";

import {
  getBusinesses,
  getDentitionOptionsByAgeId,
  getDeploymentAgeOptionsByDeploymentIdAndSpeciesId,
  getDeploymentBreedOptionsByDeploymentIdAndSpeciesId,
  getLabels,
  getProperties,
  getSexes,
  selectActiveProducts,
} from "selectors";
import {
  calculateTotalPriceCents,
  formatDollarsFromCents,
  formatWeightKg,
} from "lib";
import { formatSplitAmount } from "lib/splits";

import { MobileTransactionalCreateSaleFormValues } from "..";
import { withNamespace } from "components/Form/FormikControls/lib";
import { FormRow } from "./WhatTab";
import { Grid } from "@material-ui/core";
import { TextArea } from "components/Form/FormikControls";
import Big from "big.js";

interface ReviewTabProps {
  deploymentId: number | undefined;
  speciesId: number | null;
  saleNamespace: string;
}

export const ReviewTab = ({
  deploymentId,
  speciesId,
  saleNamespace,
}: ReviewTabProps) => {
  const { values } =
    useFormikContext<MobileTransactionalCreateSaleFormValues>();

  const businessByIdLookup = useSelector(getBusinesses);
  const propertiesLookup = useSelector(getProperties);
  const labelsLookup = useSelector(getLabels);
  const ageOptions = useSelector(
    getDeploymentAgeOptionsByDeploymentIdAndSpeciesId(deploymentId, speciesId),
  );
  const breedOptions = useSelector(
    getDeploymentBreedOptionsByDeploymentIdAndSpeciesId(
      deploymentId,
      speciesId,
    ),
  );
  const sexes = useSelector(getSexes);
  const products = useSelector(selectActiveProducts);

  const { consignment, saleLot, skinLots, buyerSplit, vendorSplit, sale } =
    values;

  const {
    vendor_id: vendorId,
    vendor_property_id: vendorPropertyId,
    billing_reference: billingReference,
  } = consignment;
  const { date, sale_title: saleTitle } = sale;
  const {
    pricing_type_id: pricingTypeId,
    product_id: productId,
    age_id: ageId,
    breed_id: breedId,
    sex_id: sexId,
    buyer_id: buyerId,
    destination_property_id: destinationPropertyId,
    invoiceToBusinessId,
    quantity,
    quantity_progeny: quantityProgeny,
    unit_price: unitPrice,
    total_mass_grams: totalMassGrams,
    quantity_delivered: quantityDelivered,
    dentitionId,
    marks,
    labels,
    note,
    reservePrice,
    draftingAttributes,
  } = saleLot;

  const dentitionOptions = useSelector(getDentitionOptionsByAgeId(ageId));
  const dentitionLabel = dentitionOptions.find(
    d => d.value === dentitionId,
  )?.label;

  const vendorLabel = vendorId
    ? businessByIdLookup[vendorId]?.name ?? "-"
    : "Not Set";
  const vendorPropertyLabel = vendorPropertyId
    ? propertiesLookup[vendorPropertyId]?.PIC ?? "-"
    : null;

  const buyerLabel = buyerId
    ? businessByIdLookup[buyerId]?.name ?? "-"
    : "Not Set";
  const buyerPropertyLabel = destinationPropertyId
    ? propertiesLookup[destinationPropertyId]?.PIC ?? "-"
    : null;

  const invoiceToLabel = invoiceToBusinessId
    ? businessByIdLookup[invoiceToBusinessId]?.name ?? "-"
    : null;

  const ageLabel = ageOptions.find(a => a.value === ageId)?.label;
  const breedLabel = breedOptions.find(b => b.value === breedId)?.label ?? "-";
  const sexLabel = sexId ? sexes[sexId]?.name ?? "-" : null;
  const selectedSexHasProgeny = Boolean(sexes[sexId]?.hasProgeny);
  const productLabel = productId ? products[productId]?.name ?? "-" : null;

  const pricingLabel =
    LivestockSalePricingTypeDisplayNameLkp[pricingTypeId] || "-";

  const markNames = marks.map(mark => mark?.location).filter(Boolean);

  const labelNames = labels
    .map(id => labelsLookup[id]?.name ?? id)
    .filter(Boolean);

  return (
    <>
      <Column gap={0} style={{ paddingTop: 8 }}>
        {/* WHO */}
        <ReviewCard>
          <ReviewCardTitle>Who</ReviewCardTitle>

          <ReviewFieldGrid>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Vendor</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {vendorLabel}
              </Text>
            </Column>
            {vendorPropertyLabel && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Vendor PIC</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {vendorPropertyLabel}
                </Text>
              </Column>
            )}
          </ReviewFieldGrid>

          {vendorSplit && vendorSplit.splits.length > 0 && (
            <ReviewFieldGrid>
              {vendorSplit.splits.map((s, i) => (
                <Column key={i} flexGrow flexShrink minWidth="100%">
                  <ReviewFieldLabel>
                    {i === 0 ? "Vendor Split" : ""}
                  </ReviewFieldLabel>
                  <Text fontSize="beta" color="gray40">
                    {businessByIdLookup[s.businessId]?.name ?? s.businessId}:{" "}
                    {formatSplitAmount(new Big(s.amount), vendorSplit!.type)}
                  </Text>
                </Column>
              ))}
            </ReviewFieldGrid>
          )}

          <ReviewFieldGrid>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Buyer</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {buyerLabel}
              </Text>
            </Column>
            {buyerPropertyLabel && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Buyer PIC</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {buyerPropertyLabel}
                </Text>
              </Column>
            )}
          </ReviewFieldGrid>

          {buyerSplit && buyerSplit.splits.length > 0 && (
            <ReviewFieldGrid>
              {buyerSplit.splits.map((s, i) => (
                <Column key={i} flexGrow flexShrink minWidth="100%">
                  <ReviewFieldLabel>
                    {i === 0 ? "Buyer Split" : ""}
                  </ReviewFieldLabel>
                  <Text fontSize="beta" color="gray40">
                    {businessByIdLookup[s.businessId]?.name ?? s.businessId}:{" "}
                    {formatSplitAmount(new Big(s.amount), buyerSplit!.type)}
                  </Text>
                </Column>
              ))}
            </ReviewFieldGrid>
          )}
        </ReviewCard>

        {/* WHAT */}
        <ReviewCard>
          <ReviewCardTitle>What</ReviewCardTitle>

          {productLabel && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="100%">
                <ReviewFieldLabel>Description</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {productLabel}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}

          <ReviewFieldGrid>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Head</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {quantity || "-"}
              </Text>
            </Column>
            {sexLabel && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Sex</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {sexLabel}
                </Text>
              </Column>
            )}
            {ageLabel && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Age</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {ageLabel}
                </Text>
              </Column>
            )}
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Breed</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {breedLabel}
              </Text>
            </Column>
            {selectedSexHasProgeny && quantityProgeny > 0 && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Progeny</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {quantityProgeny}
                </Text>
              </Column>
            )}
            {dentitionLabel && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Dentition</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {dentitionLabel}
                </Text>
              </Column>
            )}
          </ReviewFieldGrid>

          {skinLots.some(lot => lot.quantity) && (
            <>
              {skinLots.map(
                ({ quantity: skinQuantity, unitPrice: skinUnitPrice }, i) => (
                  <ReviewFieldGrid key={i}>
                    <Column flexGrow flexShrink minWidth="20%">
                      <ReviewFieldLabel>
                        {skinLots.length > 1
                          ? `Skins Lot ${i + 1} Qty`
                          : "Skins Qty"}
                      </ReviewFieldLabel>
                      <Text fontSize="beta" color="gray40">
                        {skinQuantity || "-"}
                      </Text>
                    </Column>
                    <Column flexGrow flexShrink minWidth="20%">
                      <ReviewFieldLabel>
                        {skinLots.length > 1
                          ? `Skins Lot ${i + 1} Price`
                          : "Skins Price"}
                      </ReviewFieldLabel>
                      <Text fontSize="beta" color="gray40">
                        {skinUnitPrice ? `$${skinUnitPrice}` : "-"}
                      </Text>
                    </Column>
                  </ReviewFieldGrid>
                ),
              )}
              {skinLots.length > 1 && (
                <ReviewFieldGrid>
                  <Column flexGrow flexShrink minWidth="20%">
                    <ReviewFieldLabel>Total Skins Qty</ReviewFieldLabel>
                    <Text fontSize="beta" color="gray40">
                      {skinLots.reduce(
                        (sum, { quantity: lotQty }) => sum + (lotQty || 0),
                        0,
                      )}
                    </Text>
                  </Column>
                  <Column flexGrow flexShrink minWidth="20%">
                    <ReviewFieldLabel>Total Skins Value</ReviewFieldLabel>
                    <Text fontSize="beta" color="gray40">
                      {`$${(
                        skinLots.reduce(
                          (sum, skinsLot) =>
                            sum + (calculateTotalPriceCents(skinsLot) || 0),
                          0,
                        ) / 100
                      ).toFixed(2)}`}
                    </Text>
                  </Column>
                </ReviewFieldGrid>
              )}
              {billingReference && (
                <ReviewFieldGrid>
                  <Column flexGrow flexShrink minWidth="100%">
                    <ReviewFieldLabel>Kill ID / Ref</ReviewFieldLabel>
                    <Text fontSize="beta" color="gray40">
                      {billingReference}
                    </Text>
                  </Column>
                </ReviewFieldGrid>
              )}
            </>
          )}

          {markNames.length > 0 && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="100%">
                <ReviewFieldLabel>Marks</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {markNames.join(", ")}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}

          {labelNames.length > 0 && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="100%">
                <ReviewFieldLabel>Labels</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {labelNames.join(", ")}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}

          {draftingAttributes?.tagNumber && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Tag #</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {draftingAttributes.tagNumber}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}

          {draftingAttributes?.animalName && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="100%">
                <ReviewFieldLabel>Animal Name</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {draftingAttributes.animalName}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}

          {note && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="100%">
                <ReviewFieldLabel>Note</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {note}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}
        </ReviewCard>

        {/* WHEN */}
        <ReviewCard>
          <ReviewCardTitle>When</ReviewCardTitle>
          <ReviewFieldGrid>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Sale Date</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {date || "-"}
              </Text>
            </Column>
          </ReviewFieldGrid>
        </ReviewCard>

        {/* PRICE */}
        <ReviewCard>
          <ReviewCardTitle>Price</ReviewCardTitle>
          <ReviewFieldGrid>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Pricing</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {pricingLabel}
              </Text>
            </Column>
            <Column flexGrow flexShrink minWidth="20%">
              <ReviewFieldLabel>Unit Price</ReviewFieldLabel>
              <Text fontSize="beta" color="gray40">
                {unitPrice || "-"}
              </Text>
            </Column>
            {totalMassGrams > 0 && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Total Mass</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {formatWeightKg(totalMassGrams, true)}
                </Text>
              </Column>
            )}
            {reservePrice && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Reserve Price</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {`${formatDollarsFromCents(reservePrice)}`}
                </Text>
              </Column>
            )}
            {quantityDelivered && (
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Hd Delivered</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {quantityDelivered}
                </Text>
              </Column>
            )}
          </ReviewFieldGrid>
          {invoiceToLabel && (
            <ReviewFieldGrid>
              <Column flexGrow flexShrink minWidth="20%">
                <ReviewFieldLabel>Invoice To</ReviewFieldLabel>
                <Text fontSize="beta" color="gray40">
                  {invoiceToLabel}
                </Text>
              </Column>
            </ReviewFieldGrid>
          )}
        </ReviewCard>
      </Column>
      <ReviewCard>
        <Column flexGrow flexShrink minWidth="20%">
          <ReviewFieldLabel>Sale Title</ReviewFieldLabel>
          <Text fontSize="beta" color="gray40">
            {saleTitle || "-"}
          </Text>
        </Column>
        <FormRow>
          <Grid item xs={12}>
            <TextArea
              name={withNamespace(saleNamespace, "notes")}
              label="Sale Notes"
              rows={4}
            />
          </Grid>
        </FormRow>
      </ReviewCard>
    </>
  );
};
